import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, ShieldCheck, Printer, CheckCircle2, Clock, ArrowDownCircle, ArrowUpCircle, AlertTriangle, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : "—");

function TermsBox({ sections, version }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white max-h-80 overflow-y-auto p-5 space-y-4" data-testid="stokvel-terms-box">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">BTMEDU Stationery Stokvel — Terms and Conditions (version {version})</p>
      {sections.map((s) => (
        <div key={s.num}>
          <p className="text-sm font-bold text-navy">{s.num}. {s.title}</p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.body}</p>
        </div>
      ))}
    </div>
  );
}

// The member-facing Stokvel wallet. Lives in the customer profile ("Stokvel Wallet")
// and on the public /stokvel page ("Stationery Stokvel"). Banking details only render
// where showBanking is true — the profile. Never anywhere public.
export function StokvelWallet({ showBanking = true, brand = "Stationery Stokvel" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [terms, setTerms] = useState(null);
  const [data, setData] = useState(null);
  const [accept, setAccept] = useState(false);
  const [activating, setActivating] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const load = () => api.get("/stokvel/me").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/stokvel/terms").then((r) => setTerms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user]);

  const activate = async () => {
    setActivating(true);
    try {
      await api.post("/stokvel/activate", { accept });
      toast.success("Stokvel wallet activated — your unique payment reference is ready");
      setAccept(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActivating(false);
    }
  };

  if (user === false)
    return (
      <div className="max-w-xl mx-auto px-4 py-16" data-testid="stokvel-login-prompt">
        <div className="text-center">
          <span className="w-14 h-14 rounded-2xl bg-gold/15 text-gold flex items-center justify-center mx-auto"><Wallet size={26} /></span>
          <h1 className="font-display text-2xl font-bold text-navy mt-5">{brand}</h1>
        </div>
        <div className="mt-6 bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8" data-testid="stokvel-about-page">
          <h2 className="font-display text-lg font-bold text-navy">What is the Stationery Stokvel?</h2>
          <p className="text-sm font-bold text-navy mt-3 leading-relaxed" data-testid="stokvel-about-hook-page">
            If You Can Save for Alcohol, vacations, get togethers and Groceries, Surely You Can Save for Your Children's Future!
            We find money for the things we want today. But what about the things our children need tomorrow?
            Your child's education should never be an afterthought. Don't wait for January to struggle with stationery expenses. Start saving today!
          </p>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">
            The BTMEDU Stationery Stokvel helps parents and customers plan ahead for stationery expenses. Save for as long as you want, contribute monthly or whenever you can, and watch your savings grow. When you're ready, use your accumulated Stokvel balance to purchase stationery and eligible products exclusively from BTMEDU.
          </p>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">
            You can save throughout the year and use your savings at the end of the year for the new school year, or shop whenever you have accumulated enough.
          </p>
        </div>
        <p className="text-sm text-slate-500 mt-6 text-center">Create a free account or sign in to activate your wallet.</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate("/auth?mode=register&next=/stokvel")} className="h-12 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors gold-glow" data-testid="stokvel-register-btn">
            Register Now
          </button>
          <button onClick={() => navigate("/auth?next=/stokvel")} className="h-12 px-8 rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold text-sm transition-colors" data-testid="stokvel-login-btn">
            Login
          </button>
        </div>
      </div>
    );

  if (!user || !data || !terms)
    return <div className="py-16 text-center text-sm text-slate-400">Loading your Stokvel wallet…</div>;

  const needsActivation = !data.activated || data.needs_reaccept;

  if (needsActivation)
    return (
      <Reveal>
        <div className="bg-white rounded-3xl border border-slate-200/80 p-8" data-testid="stokvel-activate-card">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-gold/15 text-gold flex items-center justify-center shrink-0"><Wallet size={22} /></span>
            <div>
              <h1 className="font-display text-2xl font-bold text-navy">
                {data.needs_reaccept ? "Updated Stokvel Terms" : "Activate your Stokvel Wallet"}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {data.needs_reaccept
                  ? "Our Stokvel Terms and Conditions have changed. Please read and accept them again to keep using your wallet."
                  : "Save towards school stationery. Read the Terms and Conditions, tick the box, and your wallet with a unique EFT payment reference is created instantly."}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <TermsBox sections={terms.sections} version={terms.version} />
            <button onClick={() => window.print()} className="mt-2 text-[11px] font-bold text-navy hover:text-gold flex items-center gap-1.5 transition-colors" data-testid="stokvel-print-terms">
              <Printer size={12} /> Print / save these terms
            </button>
          </div>

          <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="stokvel-accept-label">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#E8722A]" data-testid="stokvel-accept-checkbox" />
            <span className="text-sm text-slate-700">I have read, understood and agree to the BTMEDU Stationery Stokvel Terms and Conditions.</span>
          </label>
          <p className="mt-3 text-[11px] text-slate-500 bg-cream/70 border border-gold/20 rounded-xl px-3.5 py-2.5" data-testid="stokvel-fee-note">
            A <span className="font-bold text-navy">once-off R50 administration fee</span> is deducted from your first verified contribution and does not count towards your balance — e.g. contribute R200 and your wallet starts at R150.
          </p>

          <button onClick={activate} disabled={!accept || activating}
            className="mt-5 w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="stokvel-activate-btn">
            <ShieldCheck size={16} /> {activating ? "Activating…" : data.needs_reaccept ? "Accept updated terms" : "Activate Stokvel Wallet"}
          </button>
          <p className="text-[11px] text-slate-400 mt-3 text-center">Your acceptance is recorded securely with the date and terms version.</p>
        </div>
      </Reveal>
    );

  return (
    <>
      <Reveal>
        <div className="bg-navy text-white rounded-3xl p-8 relative overflow-hidden" data-testid="stokvel-dashboard">
          <div className="absolute inset-0 hero-grid-lines opacity-50" />
          <div className="relative">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{brand} · Active</p>
                <p className="font-display text-4xl font-extrabold mt-2" data-testid="stokvel-balance">{formatR(data.balance)}</p>
                <p className="text-xs text-white/60 mt-1">Available purchasing balance</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">Your unique EFT reference</p>
                <p className="font-mono text-2xl font-extrabold tracking-widest text-gold mt-1" data-testid="stokvel-reference">{data.wallet.reference}</p>
                <p className="text-[11px] text-white/50 mt-1">Member since {fmtDate(data.wallet.created_at)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="bg-white/10 rounded-2xl p-3.5" data-testid="stokvel-contributed">
                <p className="text-[9px] font-bold uppercase tracking-wider text-white/50">Verified contributions</p>
                <p className="font-mono font-bold mt-1">{formatR(data.contributed)}</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-3.5" data-testid="stokvel-pending">
                <p className="text-[9px] font-bold uppercase tracking-wider text-white/50">Pending verification</p>
                <p className="font-mono font-bold mt-1">{formatR(data.pending)}</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-3.5" data-testid="stokvel-spent">
                <p className="text-[9px] font-bold uppercase tracking-wider text-white/50">Spent on purchases</p>
                <p className="font-mono font-bold mt-1">{formatR(data.spent)}</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-5 bg-cream border border-gold/30 rounded-2xl p-5" data-testid="stokvel-notice">
          <p className="text-xs text-navy leading-relaxed">
            <span className="font-bold">Good to know:</span> your BTMEDU Stationery Stokvel balance is intended for eligible purchases from BTMEDU only. It is not a general-purpose cash wallet — withdrawals and cash conversions are not supported, subject to applicable legal rights.
          </p>
        </div>
      </Reveal>

      {showBanking && (
        <Reveal delay={0.12}>
          <div className="mt-5 bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="stokvel-banking-card">
            <h2 className="font-display font-bold text-navy flex items-center gap-2"><Landmark size={16} /> Making an EFT contribution</h2>
            {data.banking_details ? (
              <div className="mt-3 text-sm text-slate-700 whitespace-pre-line rounded-2xl bg-cream/70 border border-gold/20 p-4" data-testid="stokvel-banking-details">{data.banking_details}</div>
            ) : (
              <p className="mt-3 text-sm text-slate-500" data-testid="stokvel-banking-pending">
                BTMEDU will publish the EFT banking details here shortly. When you make an EFT, always quote your unique reference <span className="font-mono font-bold text-navy">{data.wallet.reference}</span> so the money is allocated to your wallet. Contributions show as verified once BTMEDU confirms receipt with the bank.
              </p>
            )}
            <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5"><AlertTriangle size={11} className="text-amber-500" /> EFTs without your exact reference may need manual investigation before they reach your wallet.</p>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.16}>
        <div className="mt-5 bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="stokvel-statement">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="font-display font-bold text-navy">Your statement</h2>
            <button onClick={() => setShowTerms(!showTerms)} className="text-[11px] font-bold text-navy hover:text-gold transition-colors" data-testid="stokvel-view-terms">
              {showTerms ? "Hide terms" : `View accepted terms (v${data.wallet.terms_version}, accepted ${fmtDate(data.wallet.accepted_at)})`}
            </button>
          </div>
          {showTerms && (
            <div className="mb-5">
              <TermsBox sections={terms.sections} version={terms.version} />
              <button onClick={() => window.print()} className="mt-2 text-[11px] font-bold text-navy hover:text-gold flex items-center gap-1.5 transition-colors" data-testid="stokvel-print-terms-2">
                <Printer size={12} /> Print / save these terms
              </button>
            </div>
          )}
          {data.ledger.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="stokvel-empty-ledger">No transactions yet — your contributions and Stokvel purchases will appear here.</p>
          ) : (
            <div className="space-y-2" data-testid="stokvel-ledger">
              {data.ledger.map((e) => (
                <div key={e.id} className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0 text-sm" data-testid={`stokvel-entry-${e.id}`}>
                  <span className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${e.amount >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                    {e.amount >= 0 ? <ArrowUpCircle size={15} /> : <ArrowDownCircle size={15} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy truncate">{e.note}</p>
                    <p className="text-[11px] text-slate-400">{fmtDate(e.created_at)} · {e.order_number || e.reference}</p>
                  </div>
                  {e.status === "pending" && (
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0"><Clock size={10} /> Pending</span>
                  )}
                  <span className={`font-mono font-bold shrink-0 ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {e.amount >= 0 ? "+" : ""}{formatR(e.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-5 flex items-center gap-3 bg-white rounded-2xl border border-slate-200/80 p-5">
          <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
          <p className="text-xs text-slate-600">When you have enough balance, <span className="font-bold text-navy">Pay using Stokvel balance</span> appears on your order page after checkout. <Link to="/shop" className="font-bold text-brand hover:text-brand-accent" data-testid="stokvel-shop-link">Start shopping</Link></p>
        </div>
      </Reveal>
    </>
  );
}
